<template>
  <div class="animated fadeIn">
    <b-card>
      <h1 class="mb-5">Create License</h1>
      <h3>License Details</h3>

      <form @submit.prevent="onSubmit">
        <b-row>
          <b-col class="border-right">
            <label>
              <span>Email or Username</span>
              <b-input
                v-model="userSearch"
                debounce="500"
                placeholder="search..."
                autocomplete="off"
                autofocus
              />
            </label>
            <b-table
              ref="userTable"
              :fields="[
                { key: 'name', label: 'Username' },
                { key: 'email', label: 'Email' },
                { key: 'org_name', label: 'Org' }
              ]"
              :items="searchUsers"
              :filter="userSearch"
              :current-page="userPage"
              :per-page="perPage"
              hover
              selectable
              select-mode="single"
              @row-selected="selectUser"
            />
            <b-pagination
              v-model="userPage"
              :total-rows="userTotal"
              :per-page="perPage"
              align="center"
              prev-text="Prev"
              next-text="Next"
              hide-goto-end-buttons
            />
          </b-col>
          <b-col>
            <label>
              <span>Organization</span>
              <b-input
                v-model="orgSearch"
                debounce="500"
                placeholder="search..."
                autocomplete="off"
              />
            </label>
            <b-table
              ref="orgTable"
              :fields="[
                { key: 'name', label: 'Name' },
                { key: 'members', label: 'Members' }
              ]"
              :items="searchOrgs"
              :filter="orgSearch"
              :current-page="orgPage"
              :per-page="perPage"
              hover
              selectable
              select-mode="single"
              @row-selected="selectOrg"
            />
            <b-pagination
              v-model="orgPage"
              :total-rows="orgTotal"
              :per-page="perPage"
              align="center"
              prev-text="Prev"
              next-text="Next"
              hide-goto-end-buttons
            />
          </b-col>
        </b-row>
        <div
          v-if="$v.license.email.$error || $v.license.org_id.$error"
          class="text-danger"
        >
          Must select either a user or an org
        </div>

        <hr />

        <NanInput
          v-model="license.type"
          :validation="$v.license.type"
          :options="licenseTypes"
          label="Type"
          type="select"
          class="mb-3"
        />

        <NanInput
          v-model="license.quantity"
          :validation="$v.license.quantity"
          placeholder="1"
          label="Quantity"
          type="number"
          class="mb-3"
        />

        <NanInput
          v-model="license.expired_time"
          :validation="$v.license.expired_time"
          placeholder="mm/dd/yyyy"
          label="Expiration"
          type="date"
          class="mb-3"
        />

        <div class="mb-4">
          License Config
          <LicenseConfigure
            :license="license"
            no-request
            @update="license.data = $event.data"
          />
        </div>

        <b-button
          @click="$v.$touch"
          :class="{ disabled: $v.$invalid }"
          :disabled="loading"
          variant="primary"
          type="submit"
        >
          <template v-if="$v.$invalid">
            Submit
          </template>
          <template v-else>
            Create
            {{ license.quantity }}
            {{ license.email ? "user" : "org" }}
            {{ license.type }} license{{ license.quantity > 1 ? "s" : "" }}
          </template>
        </b-button>
      </form>
    </b-card>
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import {
  required,
  requiredIf,
  email,
  integer,
  minValue
} from "vuelidate/lib/validators";
import API from "@/api";

import LicenseConfigure from "@/components/licenses/LicenseConfigure";

export default {
  components: {
    LicenseConfigure
  },

  mixins: [validationMixin],

  data: () => ({
    license: {
      email: undefined,
      org_id: undefined,
      type: "edu",
      quantity: 1,
      expired_time: "",
      data: {}
    },
    licenseTypes: [
      { label: "Pro", value: "pro" },
      { label: "Academic", value: "edu" },
      { label: "Enterprise", value: "enterprise" }
    ],
    loading: false,
    perPage: 5,
    orgPage: 1,
    orgSearch: "",
    orgTotal: 0,
    userPage: 1,
    userSearch: "",
    userTotal: 0
  }),

  watch: {
    orgSearch() {
      this.orgPage = 1;
    },
    userSearch() {
      this.userPage = 1;
    }
  },

  mounted() {
    const d = new Date();
    d.setYear(d.getFullYear() + 1);
    this.license.expired_time = d.toISOString().slice(0, 10);
  },

  methods: {
    async onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid || this.loading) return;

      this.loading = true;
      const { success } = await API.createLicense(this.license);
      this.loading = false;

      if (success) {
        this.$notify({
          type: "success",
          text: "License(s) created"
        });
        this.license.email = undefined;
        this.license.org_id = undefined;
        this.$refs.orgTable.clearSelected();
        this.$refs.userTable.clearSelected();
        this.$v.$reset();
      }
    },

    async searchOrgs(ctx) {
      const { success, results } = await API.adminOrgSearch(
        this.orgSearch,
        ctx.currentPage,
        ctx.perPage
      );
      if (!success) return;

      this.orgTotal = results.total;
      return results.results;
    },

    async searchUsers(ctx) {
      const { success, results } = await API.adminUserSearch(
        this.userSearch,
        ctx.currentPage,
        ctx.perPage
      );
      if (!success) return;

      this.userTotal = results.total;
      return results.results;
    },

    selectOrg(event) {
      const item = event[0];
      if (item) this.$refs.userTable.clearSelected();
      this.license.org_id = item && item.id;
    },

    selectUser(event) {
      const item = event[0];
      if (item) this.$refs.orgTable.clearSelected();
      this.license.email = item && item.email;
    }
  },

  validations: {
    license: {
      type: { required },
      email: { required: requiredIf(vm => !vm.org_id), email },
      org_id: { required: requiredIf(vm => !vm.email) },
      quantity: {
        required,
        integer,
        minValue: minValue(1)
      },
      expired_time: { required }
    }
  }
};
</script>
